import Decimal from 'decimal.js'
import { match } from 'ts-pattern'
import type { Effort, Limit } from '@/lib/calculateEffort'
import { format } from 'date-fns'

export type CalcKind =
  | 'RATIO'
  | 'EFFORT'
  | 'SUM_EFFORT'
  | 'HOURS'
  | 'SUM_HOURS'
  | 'DAYS'
  | 'SUM_DAYS'

interface BaseUnitTime {
  actual: number
}

interface TenantLimitUnitTime extends BaseUnitTime {
  tenantLimit: number
}

interface UserLimitUnitTime extends BaseUnitTime {
  userLimit: number
}

interface TargetPeriod {
  targetPeriod: string
}

export type UnitTimePerPeriodBase = { [date: string]: BaseUnitTime }
export type UnitTimePerPeriodWithTenantLimit = { [date: string]: TenantLimitUnitTime }
export type UnitTimePerPeriodWithUserLimit = { [date: string]: UserLimitUnitTime }
export type UnitTimePerPeriodLimit = UnitTimePerPeriodWithTenantLimit &
  UnitTimePerPeriodWithUserLimit
export type UnitTimePerPeriod = UnitTimePerPeriodBase & UnitTimePerPeriodLimit

export type FParams =
  | ({
      calcKind: 'RATIO'
      unitTimePerPeriod: UnitTimePerPeriodWithUserLimit
      dailyWorkTime: number
    } & TargetPeriod)
  | ({
      calcKind: 'EFFORT'
      unitTimePerPeriod: UnitTimePerPeriodWithTenantLimit
      dailyWorkTime: number
    } & TargetPeriod)
  | ({
      calcKind: 'HOURS' | 'DAYS'
      unitTimePerPeriod: UnitTimePerPeriodBase
      dailyWorkTime: number
    } & TargetPeriod)
  | {
      calcKind: 'SUM_EFFORT'
      unitTimePerPeriod: UnitTimePerPeriodWithTenantLimit
      dailyWorkTime: number
    }
  | {
      calcKind: 'SUM_HOURS' | 'SUM_DAYS'
      unitTimePerPeriod: UnitTimePerPeriodBase
      dailyWorkTime: number
    }

const calcActualDivUserLimit = (
  unitTimePerPeriod: UnitTimePerPeriodWithUserLimit,
  targetPeriod: string
) => {
  if (!(targetPeriod in unitTimePerPeriod)) return new Decimal(0)
  const targetUnitTime = unitTimePerPeriod[targetPeriod]
  const actual = new Decimal(targetUnitTime.actual)
  const userLimit = new Decimal(targetUnitTime.userLimit)
  // 0除算は1.0を返す
  if (userLimit.isZero()) return new Decimal(1)
  return actual.div(userLimit)
}

const calcActualDivTenantLimit = (
  unitTimePerPeriod: UnitTimePerPeriodWithTenantLimit,
  targetPeriod: string
) => {
  if (!(targetPeriod in unitTimePerPeriod)) return new Decimal(0)
  const targetUnitTime = unitTimePerPeriod[targetPeriod]
  const actual = new Decimal(targetUnitTime.actual)
  const tenantLimit = new Decimal(targetUnitTime.tenantLimit)
  if (tenantLimit.isZero()) {
    // 営業日外は0を返す
    if (actual.isZero()) return new Decimal(0)
    // 営業日外で計画・稼働がついている場合は1を返す
    return new Decimal(1)
  }
  return actual.div(tenantLimit)
}

const calcSumActualDivTenantLimit = (unitTimePerPeriod: UnitTimePerPeriodWithTenantLimit) => {
  let sumReal = new Decimal(0)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Object.entries(unitTimePerPeriod).forEach(([key, _]) => {
    sumReal = sumReal.add(calcActualDivTenantLimit(unitTimePerPeriod, key))
  })
  return sumReal
}

const calcHours = (unitTimePerPeriod: UnitTimePerPeriodBase, targetPeriod: string) => {
  if (!(targetPeriod in unitTimePerPeriod)) return new Decimal(0)
  const targetUnitTime = unitTimePerPeriod[targetPeriod]
  const actual = new Decimal(targetUnitTime.actual)
  return actual.div(new Decimal(3600))
}

const calcSumDays = (unitTimePerPeriod: UnitTimePerPeriodBase, dailyWorkTime: number) => {
  let sumReal = new Decimal(0)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Object.entries(unitTimePerPeriod).forEach(([key, _]) => {
    sumReal = sumReal.add(calcDays(unitTimePerPeriod, key, dailyWorkTime))
  })
  return sumReal
}

const calcDays = (
  unitTimePerPeriod: UnitTimePerPeriodBase,
  targetPeriod: string,
  dailyWorkTime: number
) => {
  if (!(targetPeriod in unitTimePerPeriod)) return new Decimal(0)
  const targetUnitTime = unitTimePerPeriod[targetPeriod]
  const actual = new Decimal(targetUnitTime.actual)
  return actual.div(new Decimal(dailyWorkTime))
}

const calcSumHours = (unitTimePerPeriod: UnitTimePerPeriodBase) => {
  let sumReal = new Decimal(0)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Object.entries(unitTimePerPeriod).forEach(([key, _]) => {
    sumReal = sumReal.add(calcHours(unitTimePerPeriod, key))
  })
  return sumReal
}

/*
FParamsの例
※各値は秒

----------------------------------------
比率を計算する場合
----------------------------------------
{
  calcKind: 'RATIO'
  unitTimePerPeriod: {
    '2023-11-01': {
      actual: 576000,
      userLimit: 576000
    },
    '2023-12-01': {
      actual: 576000,
      userLimit: 547200
    },
    ...
  },
  targetPeriod: '2023-12-01'
}

----------------------------------------
工数(単位時間ベース)を計算する場合
----------------------------------------
{
  calcKind: 'EFFORT'
  unitTimePerPeriod: {
    '2023-11-01': {
      actual: 576000,
      tenantLimit: 576000
    },
    '2023-12-01': {
      actual: 576000,
      tenantLimit: 547200
    },
    ...
  },
  targetPeriod: '2023-12-01'
}


----------------------------------------
工数(単位時間ベース)合計を計算する場合
----------------------------------------
{
  calcKind: 'EFFORT'
  unitTimePerPeriod: {
    '2023-11-01': {
      actual: 576000,
      tenantLimit: 576000
    },
    '2023-12-01': {
      actual: 576000,
      tenantLimit: 547200
    },
    ...
  }
}
*/

/*
  指定したCalcKindに応じて計算結果を返す
  - RATIO : 比率
  - EFFORT : 単位時間あたりの稼働日ベースの工数(人月/人週/人日)
  - SUM_EFFORT : 単位時間あたりの稼働日ベースの工数合計(人月/人週/人日)
  - HOURS : 時間
  - SUM_HOURS : 時間の合計
  - DAYS : 日（時間をテナントの標準稼働時間/1日で割った値 ≒ 人日）
  - SUM_HOURS : 日の合計
*/
export const calcUnitTime = (params: FParams): Decimal => {
  const v = match(params)
    .with({ calcKind: 'RATIO' }, (params) => {
      return calcActualDivUserLimit(params.unitTimePerPeriod, params.targetPeriod)
    })
    .with({ calcKind: 'EFFORT' }, (params) => {
      return calcActualDivTenantLimit(params.unitTimePerPeriod, params.targetPeriod)
    })
    .with({ calcKind: 'SUM_EFFORT' }, (params) => {
      return calcSumActualDivTenantLimit(params.unitTimePerPeriod)
    })
    .with({ calcKind: 'HOURS' }, (params) => {
      return calcHours(params.unitTimePerPeriod, params.targetPeriod)
    })
    .with({ calcKind: 'SUM_HOURS' }, (params) => {
      return calcSumHours(params.unitTimePerPeriod)
    })
    .with({ calcKind: 'DAYS' }, (params) => {
      return calcDays(params.unitTimePerPeriod, params.targetPeriod, params.dailyWorkTime)
    })
    .with({ calcKind: 'SUM_DAYS' }, (params) => {
      return calcSumDays(params.unitTimePerPeriod, params.dailyWorkTime)
    })
    .exhaustive()
  return v
}

// 表示用の関数
export type FormatType =
  | 'MAN_MONTH'
  | 'MAN_DAY'
  | 'MAN_WEEK'
  | 'RATIO'
  | 'RATIO_ONE_DECIMAL'
  | 'HOURS'
  | 'PLAN_HOURS'
  | 'PLAN_MINUTES'
export const formatForDisplay: { [K in FormatType]: (n: Decimal | number) => string } = {
  MAN_MONTH: (n) => n.toFixed(2),
  MAN_WEEK: (n) => n.toFixed(1),
  MAN_DAY: (n) => n.toFixed(1),
  RATIO: (n) => {
    const v = n instanceof Decimal ? n.mul(100) : n * 100
    return v.toFixed(0)
  },
  RATIO_ONE_DECIMAL: (n) => {
    const v = n instanceof Decimal ? n.mul(100) : n * 100
    // RATIO_ONE_DECIMAL の定義として、小数点がない場合は整数の形式で返すものとする
    return parseFloat(v.toFixed(1)).toString()
  },
  HOURS: (n) => n.toFixed(1),
  PLAN_HOURS: (n) => n.toFixed(2),
  PLAN_MINUTES: (n) => n.toFixed(0)
}

export const convertUnitTimePerPeriod = (efforts: Effort, limits: Limit) => {
  const unitTimePerPeriod: UnitTimePerPeriod = {}
  limits.forEach((limit) => {
    const effort = efforts.find((e) => e.start.getTime() === limit.start.getTime())
    const date = format(limit.start, 'yyyy-MM-dd')
    unitTimePerPeriod[date] = {
      actual: effort ? effort.actual.seconds : 0,
      tenantLimit: limit.tenantLimit,
      userLimit: limit.userLimit
    }
  })
  return unitTimePerPeriod
}
